
import { HttpErrorResponse } from '@angular/common/http';
import { Component, output } from '@angular/core';
import { FormsModule } from '@angular/forms';
import { removeFromArray } from '@reflact/tsutil';
import { ButtonModule } from 'primeng/button';
import { DialogModule } from 'primeng/dialog';
import { InputTextModule } from 'primeng/inputtext';
import { ListboxModule } from 'primeng/listbox';
import { ProgressSpinnerModule } from 'primeng/progressspinner';
import { TooltipModule } from 'primeng/tooltip';
import { ToastrService, ToastErrorTitle } from '@shared-angular/services/toastr.service';
import { DocSearchWebsiteService } from '../../../../../../../shared/services/docsearch-website.service';

@Component({
  selector: 'app-website-button',
  standalone: true,
  imports: [
    FormsModule,
    ButtonModule,
    DialogModule,
    ListboxModule,
    ProgressSpinnerModule,
    InputTextModule,
    TooltipModule
  ],
  templateUrl: './website-button.html',
  styleUrls: ['./website-button.scss']
})
export class WebsiteButtonComponent {
  public readonly fileSelected = output();

  protected displayFileBrowser = false;
  protected loading = false;
  protected processing = false;

  // Replace-Modus: gesetzt wenn ein bestehendes Dokument ersetzt werden soll
  private replaceDocId: string | null = null;
  protected get isReplaceMode(): boolean { return this.replaceDocId !== null; }


  // Tab 2 - Web Pages
  protected selectedPages: string[] = [];

  protected _selectedPage?: string | null;
  protected links: string[] = [];
  protected linkSearchQuery = '';

  protected activeTab = 0;
  protected urlInput = '';

  protected get isValidUrl(): boolean {
    try {
      const url = new URL(this.urlInput.trim());
      return url.protocol === 'http:' || url.protocol === 'https:';
    } catch {
      return false;
    }
  }




  protected get btnLabel(): string {
    if (this.processing) return $localize`:@@processing:Verarbeiten...`;
    return this.isReplaceMode ? $localize`:@@website-button-replace:Website (Ersetzen)` : $localize`:@@website-button:Website`;
  }

  protected get btnIcon(): string {
    return this.processing ? 'ri-loader-2-line spinning-icon' : 'ri-earth-line';
  }

  constructor(protected docWebsiteService: DocSearchWebsiteService, private toastr: ToastrService) { }

  /** Öffnet den Dialog im Replace-Modus für ein bestehendes Dokument */
  public openForReplace(docId: string) {
    this.replaceDocId = docId;
    this.displayFileBrowser = true;
  }

  public async showLinksForpage(page: string) {
    this.loading = true;
    try {
      this._selectedPage = page;
      const urls = await this.docWebsiteService.getLinksForUrl(page);
      this.links = urls;
    } catch (e) {
      this.links = [];
      this.showRequestError(e, $localize`:@@website-links-error:Die Links dieser Seite konnten nicht geladen werden.`);
    }
    this.loading = false;
  }

  protected get filterdLinks(): string[] {
    if (!this.linkSearchQuery.trim()) return this.links;
    const q = this.linkSearchQuery.toLowerCase();
    return this.links.filter(page => page.toLowerCase().includes(q));
  }

  protected addPageToSelectedPages(url: string) {
    if (!this.selectedPages.includes(url)) {
      this.selectedPages.push(url);
      if (this.selectedPages.length == 1) {
        void this.showLinksForpage(url);
      }
    }
  }

  protected cancel() {
    this.displayFileBrowser = false;
    this.replaceDocId = null;
  }

  protected removePageFromSelectedPages(url: string) {
    removeFromArray(this.selectedPages, (p => p == url));
    if (url == this._selectedPage) {
      this._selectedPage = null;
      this.links = [];
    }
  }


  protected async submit() {
    this.displayFileBrowser = false;
    this.processing = true;
    const docId = this.replaceDocId;

    this.replaceDocId = null;
    try {
      if (docId) {
        await this.docWebsiteService.replacePdfFromUrls(this.selectedPages, docId);
      } else {
        await this.docWebsiteService.createPdfFromUrls(this.selectedPages);
      }
      // TODO: The 'emit' function requires a mandatory void argument
      this.fileSelected.emit();
    } catch (e) {
      this.showRequestError(e, $localize`:@@website-create-error:Die Website konnte nicht als Dokument gespeichert werden.`);
    } finally {
      this.processing = false;
    }
  }

  private showRequestError(e: unknown, fallback: string) {
    if (e instanceof HttpErrorResponse) {
      const title = this.toastTitleForStatus(e.status);
      const serverMessage = (e.error as { message?: string } | null)?.message;
      this.toastr.error(title, serverMessage ?? fallback);
      return;
    }
    this.toastr.error('ERROR', fallback);
  }

  private toastTitleForStatus(status: number): keyof typeof ToastErrorTitle {
    switch (status) {
      case 400: return 'ERROR_400';
      case 401: return 'ERROR_401';
      case 403: return 'ERROR_403';
      case 404: return 'ERROR_404';
      case 429: return 'ERROR_429';
      default: return status >= 500 ? 'ERROR_500' : 'ERROR';
    }
  }

}
